import { useCallback, useEffect, useState } from "react";
import "@/App.css";
import { Zap } from "lucide-react";
import { CARDS, askPushPermission, fetchResults, runAll, runOne, runStream } from "./lib/api";
import { ConnectorCard } from "./components/ConnectorCard";
import { ResultsTable } from "./components/ResultsTable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initial = Object.fromEntries(CARDS.map((c) => [c.key, { status: "IDLE", result: null, stream: null }]));

export default function App() {
  const [cards, setCards] = useState(initial);
  const [meta, setMeta] = useState({});
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState("");
  const [allRunning, setAllRunning] = useState(false);

  const refresh = useCallback(() => fetchResults().then(setRows).catch(() => null), []);
  const patch = (key, v) => setCards((s) => ({ ...s, [key]: { ...s[key], ...v } }));

  useEffect(() => {
    fetch(`${API}/connectors`).then((r) => r.json()).then((l) => setMeta(Object.fromEntries(l.map((c) => [c.key, c]))));
    refresh();
  }, [refresh]);

  const run = async (key) => {
    patch(key, { status: "RUNNING", result: null, stream: key === "stream_chat" ? "" : null });
    let result;
    try {
      if (key === "stream_chat") {
        result = await runStream((t) => setCards((s) => ({ ...s, stream_chat: { ...s.stream_chat, stream: s.stream_chat.stream + t } })));
      } else {
        const params = { email };
        if (key === "push") params.push_permission = await askPushPermission();
        result = await runOne(key, params);
      }
    } catch (e) {
      result = { status: "FAIL", error: `Browser fetch error: ${e.message}`, latency_ms: 0, http_status: null };
    }
    patch(key, { status: result.status, result });
    refresh();
  };

  const runEverything = async () => {
    setAllRunning(true);
    const push_permission = await askPushPermission();
    setCards(Object.fromEntries(CARDS.map((c) => [c.key, { status: "RUNNING", result: null, stream: null }])));
    try {
      const data = await runAll({ email: email || null, push_permission });
      setCards(Object.fromEntries(data.results.map((r) => [r.connector, { status: r.status, result: r, stream: null }])));
    } catch (e) {
      setCards(Object.fromEntries(CARDS.map((c) => [c.key, { status: "FAIL", result: { error: `run-all fetch error: ${e.message}`, latency_ms: 0 }, stream: null }])));
    }
    setAllRunning(false);
    refresh();
  };

  const counts = Object.values(cards).reduce((a, c) => ({ ...a, [c.status]: (a[c.status] || 0) + 1 }), {});

  return (
    <div className="lab">
      <div className="grain" />
      <header className="hero">
        <div>
          <div className="eyebrow"><Zap size={13} /> emergent universal key · qa bench</div>
          <h1 className="title">Universal Key<br /><span>Connector Lab</span></h1>
          <p className="sub">Thirteen live connectors. Real calls, real responses, exact errors. Nothing mocked.</p>
        </div>
        <div className="hero-side">
          <div className="tally" data-testid="summary-tally">
            <span className="t-pass">{counts.PASS || 0} pass</span>
            <span className="t-fail">{counts.FAIL || 0} fail</span>
            <span className="t-run">{counts.RUNNING || 0} running</span>
          </div>
          <button data-testid="run-all-btn" onClick={runEverything} disabled={allRunning} className="btn-all">
            {allRunning ? "Running all…" : "Run all tests"}
          </button>
        </div>
      </header>

      <main className="grid-cards">
        {CARDS.map((c, i) => (
          <ConnectorCard key={c.key} index={i} card={c} meta={meta[c.key]} state={cards[c.key]}
            onRun={() => run(c.key)} email={email} setEmail={setEmail} />
        ))}
      </main>

      <ResultsTable rows={rows} />
    </div>
  );
}
